import React, { useState, useEffect } from 'react';
import { productHistoryService } from '../services/productHistoryService';
import { useLocation } from '../contexts/LocationContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import BaseModal from './common/BaseModal';

const ProductHistoryModal = ({ productId, productName, isOpen, onClose }) => {
  const { selectedLocation, locationId } = useLocation();
  const { selectedWarehouse, warehouseId } = useWarehouse();

  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtry
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    operation_type: ''
  });

  // Paginacja
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 30,
    total: 0,
    pages: 0,
    has_prev: false,
    has_next: false
  });

  useEffect(() => {
    if (isOpen && productId) {
      fetchHistory();
    }
  }, [isOpen, productId, pagination.page, pagination.per_page]);

  // Reset przy zmianie produktu
  useEffect(() => {
    if (isOpen && productId) {
      setFilters({ search: '', date_from: '', date_to: '', operation_type: '' });
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [productId, isOpen]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError('');

      const filterParams = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters
      };

      const response = await productHistoryService.getProductHistory(productId, filterParams);

      if (response.success) {
        setHistory(response.data.history || []);
        setStats(response.data.stats || {});
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.data.pagination
          }));
        }
      } else {
        setError('Nie udalo sie pobrac historii produktu');
      }
    } catch (err) {
      console.error('Blad pobierania historii:', err);
      setError('Blad podczas pobierania historii produktu');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchHistory();
  };

  const clearFilters = () => {
    setFilters({ search: '', date_from: '', date_to: '', operation_type: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(fetchHistory, 0);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, per_page: newPerPage, page: 1 }));
  };

  const formatDateTime = (date, time) => {
    if (!date) return '-';
    const dateStr = new Date(date).toLocaleDateString('pl-PL');
    const timeStr = time ? time.substring(0, 5) : '';
    return `${dateStr} ${timeStr}`.trim();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount || 0);
  };

  const getOperationIcon = (type) => {
    switch (type) {
      case 'sprzedaz': return '🛒';
      case 'faktura_zakupowa': return '📄';
      case 'przyjecie_pz': return '📥';
      case 'ruch_magazynowy': return '📦';
      case 'zmiana_ceny': return '💰';
      default: return '📋';
    }
  };

  const getOperationName = (type) => {
    switch (type) {
      case 'sprzedaz': return 'Sprzedaz';
      case 'faktura_zakupowa': return 'Faktura zakupowa (FZ)';
      case 'przyjecie_pz': return 'Przyjecie (PZ)';
      case 'ruch_magazynowy': return 'Ruch magazynowy';
      case 'zmiana_ceny': return 'Zmiana ceny';
      default: return 'Operacja';
    }
  };

  const getOperationColor = (type) => {
    switch (type) {
      case 'sprzedaz': return 'var(--success)';
      case 'faktura_zakupowa': return 'var(--danger)';
      case 'przyjecie_pz': return 'var(--purple)';
      case 'ruch_magazynowy': return 'var(--info)';
      case 'zmiana_ceny': return 'var(--warning)';
      default: return 'var(--text-muted)';
    }
  };

  const subtitleText = (selectedLocation || selectedWarehouse)
    ? `📍 ${selectedLocation?.nazwa || selectedWarehouse?.nazwa}`
    : undefined;

  const footerContent = (
    <>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginRight: 'auto' }}>
        Pokazuje {pagination.total > 0 ? ((pagination.page - 1) * pagination.per_page) + 1 : 0} - {Math.min(pagination.page * pagination.per_page, pagination.total)} z {pagination.total} operacji
      </div>

      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {/* Selector per_page */}
        <select
          value={pagination.per_page}
          onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
          style={{
            padding: '4px 8px',
            fontSize: '10px',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            backgroundColor: 'var(--card-bg)'
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>

        <button
          onClick={() => handlePageChange(1)}
          disabled={pagination.page === 1}
          style={{
            padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
            color: pagination.page === 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
            opacity: pagination.page === 1 ? 0.5 : 1
          }}
        >
          &laquo;
        </button>
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={!pagination.has_prev}
          style={{
            padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
            color: !pagination.has_prev ? 'var(--text-muted)' : 'var(--text-secondary)',
            opacity: !pagination.has_prev ? 0.5 : 1
          }}
        >
          &lsaquo;
        </button>
        <span style={{ padding: '4px 12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
          {pagination.page} / {pagination.pages || 1}
        </span>
        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={!pagination.has_next}
          style={{
            padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
            color: !pagination.has_next ? 'var(--text-muted)' : 'var(--text-secondary)',
            opacity: !pagination.has_next ? 0.5 : 1
          }}
        >
          &rsaquo;
        </button>
        <button
          onClick={() => handlePageChange(pagination.pages)}
          disabled={pagination.page === pagination.pages || pagination.pages === 0}
          style={{
            padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
            color: (pagination.page === pagination.pages || pagination.pages === 0) ? 'var(--text-muted)' : 'var(--text-secondary)',
            opacity: (pagination.page === pagination.pages || pagination.pages === 0) ? 0.5 : 1
          }}
        >
          &raquo;
        </button>
      </div>

      <button
        onClick={onClose}
        style={{
          padding: '5px 14px',
          border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
          fontSize: '12px'
        }}
      >
        Zamknij
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`📊 Historia produktu: ${productName}`}
      subtitle={subtitleText}
      size="xl"
      zIndex={1000}
      fullHeight
      noPadding
      headerStyle={{
        padding: '12px 20px',
        backgroundColor: 'var(--bg)',
        color: 'var(--text)',
        borderBottom: '1px solid var(--border)',
      }}
      footer={footerContent}
    >
      {/* Filtry */}
      <div style={{
        padding: '10px 20px',
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--bg)'
      }}>
        <form onSubmit={handleSearch}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '10px',
            marginBottom: '8px'
          }}>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '3px', display: 'block' }}>
                Numer dokumentu
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="FZ/PZ/Paragon..."
                style={{
                  width: '100%', padding: '5px 8px', fontSize: '11px',
                  border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '3px', display: 'block' }}>
                Data od
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                style={{
                  width: '100%', padding: '5px 8px', fontSize: '11px',
                  border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '3px', display: 'block' }}>
                Data do
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                style={{
                  width: '100%', padding: '5px 8px', fontSize: '11px',
                  border: '1px solid var(--border)', borderRadius: '4px', boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '3px', display: 'block' }}>
                Typ operacji
              </label>
              <select
                value={filters.operation_type}
                onChange={(e) => handleFilterChange('operation_type', e.target.value)}
                style={{
                  width: '100%', padding: '5px 8px', fontSize: '11px',
                  border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', boxSizing: 'border-box'
                }}
              >
                <option value="">Wszystkie</option>
                <option value="sprzedaz">Sprzedaz</option>
                <option value="faktura_zakupowa">Faktura zakupowa (FZ)</option>
                <option value="przyjecie_pz">Przyjecie (PZ)</option>
                <option value="ruch_magazynowy">Ruch magazynowy</option>
                <option value="zmiana_ceny">Zmiana ceny</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="submit"
              style={{
                padding: '5px 12px', border: '1px solid var(--primary)', color: 'var(--primary)',
                fontSize: '11px'
              }}
            >
              🔍 Szukaj
            </button>
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: '5px 12px', backgroundColor: 'var(--bg)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', fontSize: '11px'
              }}
            >
              ✕ Wyczysc
            </button>
          </div>
        </form>
      </div>

      {/* Stats */}
      {!loading && Object.keys(stats).length > 0 && (
        <div style={{
          padding: '10px 20px',
          backgroundColor: "var(--card-bg)",
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '10px'
          }}>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--success-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--success)' }}>
                {stats.total_sold_quantity || 0}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--success)' }}>Sprzedano szt.</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--danger-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--danger)' }}>
                {stats.total_purchased_quantity || 0}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--danger)' }}>Zakupiono szt.</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--success-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--success)' }}>
                {formatCurrency(stats.total_revenue)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--success)' }}>Przychod</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--danger-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--danger)' }}>
                {formatCurrency(stats.total_purchase_value)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--danger)' }}>Koszt zakupu</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--success-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--success)' }}>
                {formatCurrency(stats.current_sell_price_netto)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--success)' }}>Cena sprzedazy netto</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--danger-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--danger)' }}>
                {formatCurrency(stats.current_buy_price_netto)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--danger)' }}>Cena zakupu netto</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--info-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--info)' }}>
                {formatCurrency(stats.margin_unit_netto)} <span style={{ fontSize: '11px' }}>({(stats.margin_unit_percent || 0).toFixed(1)}%)</span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--info)' }}>Marza jednostkowa netto</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--orange-light)', borderRadius: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--orange)' }}>
                {formatCurrency(stats.margin_total_netto)} <span style={{ fontSize: '11px' }}>({(stats.margin_total_percent || 0).toFixed(1)}%)</span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--orange)' }}>Marza calkowita netto</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'var(--border)', borderRadius: '4px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text)' }}>
                {stats.total_operations || 0}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text)' }}>Operacji</div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
        {loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Ladowanie historii...
          </div>
        )}

        {error && (
          <div style={{
            padding: '20px',
            backgroundColor: 'var(--danger-light)',
            color: 'var(--danger)',
            border: '1px solid var(--danger-light)',
            margin: '20px'
          }}>
            {error}
          </div>
        )}

        {!loading && !error && history.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Brak historii operacji dla tego produktu
          </div>
        )}

        {!loading && !error && history.length > 0 && (
          <div style={{ padding: '0' }}>
            {history.map((item, index) => (
              <div
                key={index}
                style={{
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--bg)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: getOperationColor(item.operation_type) + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0
                }}>
                  {getOperationIcon(item.operation_type)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontWeight: 'bold',
                      fontSize: '12px',
                      color: getOperationColor(item.operation_type)
                    }}>
                      {getOperationName(item.operation_type)}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {formatDateTime(item.operation_date, item.operation_time)}
                    </span>
                    {item.customer_info && (
                      <span style={{
                        fontSize: '10px',
                        backgroundColor: 'var(--border)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        color: 'var(--text-secondary)'
                      }}>
                        {item.customer_info}
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {item.operation_type === 'sprzedaz' && (
                      <>
                        <span style={{ color: 'var(--success)', fontWeight: '600' }}>SPRZEDAZ</span> Ilosc: <strong>{item.quantity}</strong> x {formatCurrency(item.unit_price)} =
                        <strong> {formatCurrency(item.total_value)}</strong>
                        {item.payment_method && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.payment_method}</span>}
                      </>
                    )}
                    {item.operation_type === 'faktura_zakupowa' && (
                      <>
                        <span style={{ color: 'var(--danger)', fontWeight: '600' }}>ZAKUP</span> Ilosc: <strong>{item.quantity}</strong> x {formatCurrency(item.unit_price)} netto =
                        <strong> {formatCurrency(item.total_value)}</strong>
                        {item.operator_name && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.operator_name}</span>}
                      </>
                    )}
                    {item.operation_type === 'przyjecie_pz' && (
                      <>
                        <span style={{ color: 'var(--purple)', fontWeight: '600' }}>PRZYJECIE</span> Ilosc: <strong>{item.quantity}</strong>
                        {item.unit_price && <> x {formatCurrency(item.unit_price)} = <strong>{formatCurrency(item.total_value)}</strong></>}
                        {item.payment_method && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.payment_method}</span>}
                        {item.operator_name && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.operator_name}</span>}
                      </>
                    )}
                    {item.operation_type === 'ruch_magazynowy' && (
                      <>
                        Zmiana stanu: <strong style={{ color: item.quantity > 0 ? 'var(--success)' : 'var(--danger)' }}>
                          {item.quantity > 0 ? '+' : ''}{item.quantity}
                        </strong>
                        {item.payment_method && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.payment_method}</span>}
                      </>
                    )}
                    {item.operation_type === 'zmiana_ceny' && (
                      <>
                        Cena: <strong>{formatCurrency(item.unit_price)}</strong>
                        {item.payment_method && <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>• {item.payment_method}</span>}
                      </>
                    )}
                  </div>

                  {item.location_name && (
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      📍 {item.location_name}
                      {item.operator_name && item.operation_type !== 'faktura_zakupowa' && item.operation_type !== 'przyjecie_pz' &&
                        ` • 👤 ${item.operator_name}`
                      }
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default ProductHistoryModal;
